import { toRaw, watch } from 'vue';
import moment from 'moment';
import PubSub from 'pubsub-js';
import i18n from '@/plugins/i18n';
import VueHooksManager from '@/common/services/HooksManager';
import { gravityNotificationsApiService } from '@/modules/seven-gravity-notifications';
import { useGravitySettingsStore } from '@/modules/cms/gravity-settings';
import { logService } from '@/common/services/logger';
import { useSevenStore } from '@/modules/seven';
import {
  type AcceptorHookParam, acceptorsService, AcceptorAction,
} from '@/modules/acceptors';
import { useNotificationsStore, TNotificationTypeEnum } from '@/common/stores/notifications';
import { useConfigSettingsStore } from '@/modules/seven/config-settings/store';
import { errorParser } from '@/common/services/error-parser';
import { getService } from '@/common/services/ngBridge';
import { googleAnalyticsService } from '@/modules/google-analytics';
import { usePrinterStatusStore } from './printerStatusStore';
import { usePrinterStore } from './printerStore';
import { PrinterPaperStatusChangeAction, PrinterStatusCode } from './enums';
import { PrinterStatus } from './interfaces/printerMessageData';
import { handlePaperStatusWarningNotification, showPrinterErrorStatus } from './printerHelpers';

// Constants
const { t } = i18n.global;
const PAPER_LOW_NOTIFICATION_TIME_INTERVAL = 30;
const LOG_PREFIX = '[printerMessagesService]';

// Vars
let isTicketPayinOnPaperLowDisabled: boolean;
let areAcceptorsDisabledOnPaperLow: boolean;

const hasTimeForSendingNotificationPassed = () => {
  const notificationTimer = moment(Number(localStorage.getItem('notification_timer')));
  const currentTime = moment();
  const differenceInMinutes = currentTime.diff(notificationTimer, 'minutes');

  return differenceInMinutes >= PAPER_LOW_NOTIFICATION_TIME_INTERVAL;
};

const increaseNotificationCounter = () => {
  const notificationCounter = Number(sessionStorage.getItem('notification_counter'));
  sessionStorage.setItem('notification_counter', (notificationCounter + 1).toString());
};

const shouldSendSystemNotification = () => {
  const gravitySettingsStore = useGravitySettingsStore();
  const sendSystemNotificationToShop = gravitySettingsStore.getModuleDataKeyValue('print', 'sendSystemNotificationToShop');

  if (sendSystemNotificationToShop) {
    const notificationCounter = Number(sessionStorage.getItem('notification_counter'));

    if (hasTimeForSendingNotificationPassed() && notificationCounter >= 1) {
      localStorage.setItem('notification_timer', Date.now().toString());
      sessionStorage.setItem('notification_counter', '0');

      return true;
    }

    increaseNotificationCounter();
  }

  return false;
};

const sendPaperLowSystemNotification = () => {
  const { isStatusPaperLow } = usePrinterStatusStore();

  if (shouldSendSystemNotification() && isStatusPaperLow()) {
    const sevenStore = useSevenStore();
    logService.debug(`${LOG_PREFIX} Send low paper notification`);

    gravityNotificationsApiService.sendSystemNotification(
      {
        title: `print.paper_low (${sevenStore.device.name})`,
        body: `print.paper_low (${sevenStore.device.name})`,
        type: 'PaperLow',
      },
    ).catch((err) => {
      logService.error(`${LOG_PREFIX} Sending paper-low system notification failed.`, {
        upstream_message: err.message || err.response?.statusText || err.status || '',
        upstream_code: err.code || err.response?.status || '',
        code: 'T_SYSTEM_NOTIFICATION_SEND_FAILED',
      });
    });
  }
};

const validatePaperOutOnTicketPayin = (): Promise<void> => {
  const { isStatusPaperOut } = usePrinterStatusStore();

  if (isStatusPaperOut()) {
    return Promise.reject({
      message: t('printer_status_paper_out_ticket_payin_disabled'),
      code: 'T_PAYIN_VALIDATION_PRINT_PAPER_OUT',
    });
  }

  return Promise.resolve();
};

const closeAcceptorsNotifications = () => {
  const notificationsStore = useNotificationsStore();
  notificationsStore.closeNotificationWithId('acceptors-start-error-paper-low');
  notificationsStore.closeNotificationWithId('acceptors-start-error-paper-out');
  notificationsStore.closeNotificationWithId('acceptors-start-error-printer-not-selected');
  notificationsStore.closeNotificationWithId('acceptors-start-error-printer-offline');
};

const validatePaperLowOnStartAcceptors = (params: AcceptorHookParam) => {
  closeAcceptorsNotifications();

  const { isStatusPaperLow } = usePrinterStatusStore();

  if (isStatusPaperLow()) {
    params.errorResponses.push({
      id: 'acceptors-start-error-paper-low',
      message: t('printer_status_paper_low_acceptors_start_error'),
      code: 'T_BEFORE_ACCEPTORS_START_PAPER_LOW_STATUS',
    });
  }
};

const validatePaperOutOnStartAcceptors = (params: AcceptorHookParam) => {
  closeAcceptorsNotifications();

  const { isStatusPaperOut } = usePrinterStatusStore();

  if (isStatusPaperOut()) {
    params.errorResponses.push({
      id: 'acceptors-start-error-paper-out',
      message: t('printer_status_paper_out_acceptors_start_error'),
      code: 'T_BEFORE_ACCEPTORS_START_PAPER_OUT_STATUS',
    });
  }
};

const validatePrinterSelectedStateOnStartAcceptors = (params: AcceptorHookParam) => {
  closeAcceptorsNotifications();

  const { selectedPrinter } = usePrinterStore();

  if (!selectedPrinter) {
    params.errorResponses.push({
      id: 'acceptors-start-error-printer-not-selected',
      message: t('printer_status_not_selected_acceptors_start_error'),
      code: 'T_BEFORE_ACCEPTORS_START_PRINTER_NOT_SELECTED',
    });
  }
};

const validatePrinterOfflineStateOnStartAcceptors = (params: AcceptorHookParam) => {
  closeAcceptorsNotifications();

  const { isStatusOffline } = usePrinterStatusStore();

  if (isStatusOffline()) {
    params.errorResponses.push({
      id: 'acceptors-start-error-printer-offline',
      message: t('printer_offline_acceptors_start_error'),
      code: 'T_BEFORE_ACCEPTORS_START_PRINTER_OFFLINE',
    });
  }
};

const handleAcceptorsStateChange = (
  cause: PrinterPaperStatusChangeAction,
  action: AcceptorAction,
) => {
  const acceptorsStateChangeLogMessage = `${LOG_PREFIX} ${action} is called on acceptors initialization finished`;
  const acceptorsStateChangeActionFn = action === AcceptorAction.START
    ? acceptorsService.startAcceptors
    : acceptorsService.stopAcceptors;

  logService.info(
    acceptorsStateChangeLogMessage,
    {
      code: 'T_PRINTER_ACCEPTORS_REQUEST',
      details: {
        action,
        cause: `Printer${cause.charAt(0).toUpperCase() + cause.slice(1)}`,
      },
    },
  );

  // Be sure that acceptors are ready beforey trying to do any action.
  // On app boot if we receive paper low or paper out,
  // we will try to change state of acceptors that are not yet ready for communication.
  // Later, when app is ready, call to onInitializationFinished is unnecessary.
  // This could be optimized, but it is not big deal right now (promise is resolved immediately).
  acceptorsService.onInitializationFinished(action)
    .then(() => {
      acceptorsStateChangeActionFn().catch((error) => {
        logService.warn(`${LOG_PREFIX} Acceptors cannot be ${action}.`, {
          ...errorParser.parseUpstream(error),
          code: `T_PRINT_ACCEPTORS_${action}_ERR`,
        });
      });
    }).catch((err) => {
      logService.warn(`${LOG_PREFIX} Acceptors could not be ${action} because they are not initialized.`, {
        code: 'T_PRINT_ACCEPTORS_INIT_ERR',
        ...errorParser.parseUpstream(err),
      });
    });
};

const handlePrinterOnAcceptorsStateChange = (
  isStatusActive: boolean,
  cause: PrinterPaperStatusChangeAction,
  notifId: string,
) => {
  const notificationsStore = useNotificationsStore();

  if (isStatusActive) {
    handleAcceptorsStateChange(cause, AcceptorAction.STOP);
  } else {
    notificationsStore.closeNotificationWithId(notifId);
    handleAcceptorsStateChange(cause, AcceptorAction.START);
  }
};

const getIsTicketPayinOnPaperLowDisabled = () => isTicketPayinOnPaperLowDisabled;

const getPaperLowStatusNotificationMessage = () => {
  let translationKey = '';

  if (areAcceptorsDisabledOnPaperLow && isTicketPayinOnPaperLowDisabled) {
    translationKey = 'printer_status_paper_low_acceptors_ticket_payin_disabled';
  } else if (areAcceptorsDisabledOnPaperLow) {
    translationKey = 'printer_status_paper_low_acceptors_disabled';
  } else if (isTicketPayinOnPaperLowDisabled) {
    translationKey = 'printer_status_paper_low_ticket_payin_disabled';
  }

  return t(translationKey);
};

const handlePaperLowNotificationBasedOnConfig = () => {
  if (!areAcceptorsDisabledOnPaperLow && !isTicketPayinOnPaperLowDisabled) {
    return;
  }

  const notificationsStore = useNotificationsStore();
  const { isStatusPaperLow } = usePrinterStatusStore();

  if (isStatusPaperLow()) {
    notificationsStore.show({
      id: 'printer_status_paper_low',
      message: getPaperLowStatusNotificationMessage(),
      type: TNotificationTypeEnum.error,
      closeDisabled: true,
      delay: null,
    });
  } else {
    notificationsStore.closeNotificationWithId('printer_status_paper_low');
  }
};

const onPaperLowStatusChange = () => {
  const { isStatusPaperLow } = usePrinterStatusStore();
  const shouldStopAcceptorsOnPaperLow = areAcceptorsDisabledOnPaperLow && isStatusPaperLow();

  handlePrinterOnAcceptorsStateChange(
    shouldStopAcceptorsOnPaperLow,
    PrinterPaperStatusChangeAction.PaperLow,
    'acceptors-start-error-paper-low',
  );
};

const onPaperOutStatusChange = () => {
  const { isStatusPaperOut } = usePrinterStatusStore();

  handlePrinterOnAcceptorsStateChange(
    isStatusPaperOut(),
    PrinterPaperStatusChangeAction.PaperOut,
    'acceptors-start-error-paper-out',
  );
};

const onOfflineStatusChange = () => {
  const { isStatusOffline } = usePrinterStatusStore();

  handlePrinterOnAcceptorsStateChange(
    isStatusOffline(),
    PrinterPaperStatusChangeAction.Offline,
    'acceptors-start-error-printer-offline',
  );
};

const getChangedPrinterStatusAction = (
  newPrinterStatusDataValue: PrinterStatus,
  oldPrinterStatusDataValue: PrinterStatus,
): PrinterPaperStatusChangeAction | undefined => {
  const isInitialLoad = oldPrinterStatusDataValue.code === PrinterStatusCode.Undefined;

  // If we pass undefined state that was changed to any kind of valid state,
  // we must return undefined. This just prevents this function to return
  // invalid value like Offline).
  // This happens on app boot when we start with undefined state that will be patched
  // (mostly) with valid state.
  if (isInitialLoad && newPrinterStatusDataValue.code === 'ok') {
    return undefined;
  }

  if (
    newPrinterStatusDataValue.valid !== oldPrinterStatusDataValue.valid
    && !newPrinterStatusDataValue.paperLow
    && !newPrinterStatusDataValue.paperOut
  ) {
    return PrinterPaperStatusChangeAction.Offline;
  }

  if (newPrinterStatusDataValue.paperLow !== oldPrinterStatusDataValue.paperLow) {
    return PrinterPaperStatusChangeAction.PaperLow;
  }

  if (newPrinterStatusDataValue.paperOut !== oldPrinterStatusDataValue.paperOut) {
    return PrinterPaperStatusChangeAction.PaperOut;
  }

  return undefined;
};

const trackEventsByPrinterStatus = () => {
  const { isStatusPaperLow, isStatusPaperOut } = usePrinterStatusStore();

  if (isStatusPaperLow()) {
    googleAnalyticsService.trackEvent('Paper_Event', {
      event: 'Paper low',
    });
  }

  if (isStatusPaperOut()) {
    googleAnalyticsService.trackEvent('Paper_Event', {
      event: 'Paper out',
    });
  }
};

const handlePrinterPaperStatusChange = () => {
  const printerStatusStore = usePrinterStatusStore();
  watch(
    () => printerStatusStore.printerStatusData,
    (newPrinterStatusData, oldPrinterStatusData) => {
      const notificationsStore = useNotificationsStore();
      const changedPrinterStatusAction = getChangedPrinterStatusAction(
        newPrinterStatusData,
        oldPrinterStatusData,
      );
      const { valid: isStatusValid } = printerStatusStore.printerStatusData;
      logService.info(`${LOG_PREFIX} Printer status is changed, try to call status action handler`, {
        code: 'T_PRINTER_STATUS_CHANGE_ACTION',
        details: {
          oldPrinterStatusData: toRaw(oldPrinterStatusData),
          newPrinterStatusData: toRaw(newPrinterStatusData),
          changedPrinterStatusAction,
        },
      });
      /**
       * Close all previous error/warning notifications because we got
       * fresh new info about printers status
       * */
      notificationsStore.closeNotificationWithId('printer_error');
      notificationsStore.closeNotificationWithId('printer_warning');

      if (isStatusValid) {
        // although status is now valid, it could be that paper is low,
        // proceed with checks
        handlePaperStatusWarningNotification();
        handlePaperLowNotificationBasedOnConfig();
        sendPaperLowSystemNotification();
      } else {
        showPrinterErrorStatus();
      }

      trackEventsByPrinterStatus();

      /**
       * Only if the old printer status is different from the new one
       * this code will be executed, otherwise it will go in the default block
       * */
      switch (changedPrinterStatusAction) {
        case PrinterPaperStatusChangeAction.PaperLow:
          onPaperLowStatusChange();
          break;

        case PrinterPaperStatusChangeAction.PaperOut:
          onPaperOutStatusChange();
          break;

        case PrinterPaperStatusChangeAction.Offline:
          onOfflineStatusChange();
          break;

        default:
          break;
      }
    },
  );
};

const limitPaperLowMessages = () => {
  const gravitySettingsStore = useGravitySettingsStore();
  const sendSystemNotificationToShop = gravitySettingsStore.getModuleDataKeyValue('print', 'sendSystemNotificationToShop');

  if (sendSystemNotificationToShop) {
    const notificationTimer = localStorage.getItem('notification_timer');

    if (!notificationTimer) {
      localStorage.setItem('notification_timer', Date.now().toString());
    }

    const notificationCounter = sessionStorage.getItem('notification_counter');

    if (!notificationCounter) {
      sessionStorage.setItem('notification_counter', '0');
    }
  }
};

const registerListeners = () => {
  PubSub.subscribe('7T:User.AuthorizationChanged', closeAcceptorsNotifications);
};

const registerHooks = () => {
  const NgHooksManager = getService<{ getHook: Function }>('HooksManager');

  // register paper low BeforeAcceptorsStart hook only if it's configured per tenant
  if (areAcceptorsDisabledOnPaperLow) {
    VueHooksManager.BeforeAcceptorsStart.tap({
      name: 'BeforeAcceptorsStart.printerMessagesService.PaperLow',
    }, validatePaperLowOnStartAcceptors);
  }

  NgHooksManager?.getHook('BeforeTicketPayin').tapPromise({
    name: 'BeforeTicketPayin.printerMessagesService.PaperOut',
  }, validatePaperOutOnTicketPayin);

  VueHooksManager.BeforeAcceptorsStart.tap({
    name: 'BeforeAcceptorsStart.printerMessagesService.PaperOut',
  }, validatePaperOutOnStartAcceptors);

  VueHooksManager.BeforeAcceptorsStart.tap({
    name: 'BeforeAcceptorsStart.printerMessagesService.PrinterSelectedState',
  }, validatePrinterSelectedStateOnStartAcceptors);

  VueHooksManager.BeforeAcceptorsStart.tap({
    name: 'BeforeAcceptorsStart.printerMessagesService.PrinterOfflineState',
  }, validatePrinterOfflineStateOnStartAcceptors);
};

const handlePaperWarningAndErrorStatusMessages = () => {
  const { data: { printerPaperLowEffects } } = useConfigSettingsStore();
  isTicketPayinOnPaperLowDisabled = printerPaperLowEffects.disableTicketPayin;
  areAcceptorsDisabledOnPaperLow = printerPaperLowEffects.disableMoneyAcceptance;

  registerListeners();
  registerHooks();
  limitPaperLowMessages();
  handlePrinterPaperStatusChange();
};

export {
  sendPaperLowSystemNotification,
  handlePaperWarningAndErrorStatusMessages,
  getIsTicketPayinOnPaperLowDisabled,
  handlePrinterPaperStatusChange,
  onPaperLowStatusChange,
  onPaperOutStatusChange,
  validatePaperLowOnStartAcceptors,
  registerHooks,
  validatePaperOutOnTicketPayin,
  validatePaperOutOnStartAcceptors,
  handleAcceptorsStateChange,
  getChangedPrinterStatusAction,
  hasTimeForSendingNotificationPassed,
};
